<template>
  <section class="hero">
    <div class="container">
      <h1 class="stagger-headline" ref="headlineRef">
        <span
          v-for="(word, i) in headlineWords"
          :key="i"
          class="stagger-word"
          :style="{ animationDelay: `${i * 0.12}s` }"
        >{{ word }}&nbsp;</span>
      </h1>
      <p class="sub-headline" :class="{ revealed: subRevealed }" ref="subRef">
        {{ $t('hero.subheadline') }}
      </p>
      <button class="cta-button" :class="{ revealed: ctaRevealed }" @click="handleAuditRequest">
        {{ $t('hero.cta') }}
      </button>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const { t } = useI18n()

const headlineRef = ref(null)
const subRef = ref(null)
const subRevealed = ref(false)
const ctaRevealed = ref(false)

const headlineWords = computed(() => t('hero.headline').split(' '))

const handleAuditRequest = () => {
  router.push({ name: 'audit-request' })
}

let observer = null

onMounted(() => {
  // Reveal sub-headline and CTA after headline animation completes
  const delay = headlineWords.value.length * 120 + 600
  setTimeout(() => { subRevealed.value = true }, delay)
  setTimeout(() => { ctaRevealed.value = true }, delay + 300)

  // Also set up IntersectionObserver for scroll-based reveal if user scrolls away and back
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          subRevealed.value = true
          setTimeout(() => { ctaRevealed.value = true }, 300)
        }
      })
    },
    { threshold: 0.5 }
  )
  if (subRef.value) observer.observe(subRef.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<style scoped>
.hero {
  padding: 100px 20px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  color: white;
  text-align: center;
  overflow: hidden;
}

.container {
  max-width: 900px;
  margin: 0 auto;
}

/* Staggered Reveal for H1 */
.stagger-headline {
  font-size: 3rem;
  margin-bottom: 20px;
  line-height: 1.2;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.stagger-word {
  display: inline-block;
  opacity: 0;
  transform: translateY(40px) scale(0.95);
  animation: staggerReveal 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  will-change: transform, opacity;
}

@keyframes staggerReveal {
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Text Reveal for sub-headline */
.sub-headline {
  font-size: 1.5rem;
  margin-bottom: 40px;
  opacity: 0;
  line-height: 1.6;
  transform: translateY(20px);
  transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
}

.sub-headline.revealed {
  opacity: 0.9;
  transform: translateY(0);
}

/* CTA reveal */
.cta-button {
  background-color: var(--accent-color);
  color: white;
  padding: 15px 30px;
  font-size: 1.2rem;
  font-weight: bold;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  opacity: 0;
  transform: translateY(15px);
  transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
              transform 0.6s cubic-bezier(0.16, 1, 0.3, 1),
              background-color 0.3s ease;
  will-change: transform, opacity;
}

.cta-button.revealed {
  opacity: 1;
  transform: translateY(0);
}

.cta-button:hover {
  transform: translateY(-2px);
  background-color: var(--accent-dark, #0044cc);
}

@media (max-width: 768px) {
  .stagger-headline {
    font-size: 2.2rem;
  }
  .sub-headline {
    font-size: 1.2rem;
  }
}
</style>
